import type { ClientErrorReport } from '@neuroedge/shared';
import { API_BASE_URL, ApiClientError, getAccessToken } from '../api/client';

/**
 * Browser-side half of the in-house error tracking: posts a crash to
 * `POST /client-errors`, where it lands in `error_logs` (source WEB) for the
 * platform-admin "Error Logs" page.
 *
 * Uses plain `fetch` rather than `apiClient` on purpose - a report must never
 * trigger the silent-refresh retry, a toast, or (if the report itself fails)
 * another report. Best-effort: every failure is swallowed.
 *
 * Not reported: `ApiClientError` (an API call failing - a 5xx is already
 * captured server-side, a 4xx is an expected outcome) and known browser
 * noise. Capped per page load and de-duplicated, so a render loop can't
 * flood the endpoint.
 */

const MAX_REPORTS_PER_PAGE_LOAD = 20;
const IGNORED_MESSAGES = [/ResizeObserver loop/i, /^Script error\.?$/i];

let sent = 0;
const seen = new Set<string>();

function describe(error: unknown): { name?: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message || error.name, stack: error.stack };
  }
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) ?? String(error) };
  } catch {
    return { message: String(error) };
  }
}

/** Resolves to the server's correlation id for the report (a "reference" to show the user), or null. */
export async function reportClientError(
  error: unknown,
  extra: { kind: string; componentStack?: string },
): Promise<string | null> {
  if (error instanceof ApiClientError) return null;
  const { name, message, stack } = describe(error);
  if (IGNORED_MESSAGES.some((re) => re.test(message))) return null;
  const key = `${name ?? ''}|${message}`;
  if (seen.has(key) || sent >= MAX_REPORTS_PER_PAGE_LOAD) return null;
  seen.add(key);
  sent += 1;

  const body: ClientErrorReport = {
    message: message.slice(0, 2000),
    errorName: name?.slice(0, 200),
    stack: stack?.slice(0, 10_000),
    componentStack: extra.componentStack?.slice(0, 10_000),
    url: window.location.pathname,
    kind: extra.kind,
  };
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const res = await fetch(`${API_BASE_URL}/client-errors`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });
    return res.headers.get('x-correlation-id');
  } catch {
    return null;
  }
}

/** Catches what no React error boundary sees: errors in event handlers, timers, and unhandled promise rejections. */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event) => {
    void reportClientError(event.error ?? event.message, { kind: 'window.error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    void reportClientError(event.reason, { kind: 'unhandledrejection' });
  });
}

/** Test-only: reset the per-page-load cap/dedupe. */
export function resetClientErrorReporting(): void {
  sent = 0;
  seen.clear();
}
